import React from "react";
import { useAtom } from "jotai";
import * as Yup from "yup";
// mui
import {
  Box,
  Typography,
  Card,
  Stack,
  Button,
  Slider,
  Grid,
  Hidden,
} from "@mui/material";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
// state
import { vesselAtom } from "../../../shared/state/atoms";
// components
import { FormProvider, RHFSelect } from "../../../shared/components/hook-forms";
// constants
import { VESSEL_OPTIONS } from "../../../shared/utils/constants";
// stylesheets
import "./SavingsPage.css";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import useSavingsPage from "../hooks/useSavingsPage";
import useIsMountedRef from "../../../shared/hooks/useIsMountedRef";

export default function SavingsPage() {
  const { handleCalculate } = useSavingsPage();
  const isMountedRef = useIsMountedRef();
  const [vessel, setVessel] = useAtom(vesselAtom);
  const [minVesselSpeed, setMinVesselSpeed] = React.useState(0);
  const [maxVesselSpeed, setMaxVesselSpeed] = React.useState(0);

  React.useEffect(() => {
    setValue("vesselSpeed", vessel.vesselSpeed);
  }, [vessel]);

  const determineVesselImage = () => {
    switch (vessel.vesselType) {
      case "Supra/Ultramax":
        return "../../../../../../../assets/Ultramax.png";
      case "Kamsarmax":
        return "../../../../../../../assets/Kamsarmax.png";
      case "Capesize":
        return "../../../../../../../assets/Capesize.png";
      case "Newcastlemax":
        return "../../../../../../../assets/Newcastlemax.png";
      case "MR Tanker":
        return "../../../../../../../assets/MR-Tanker.png";
      case "LR Tanker":
        return "../../../../../../../assets/Newcastlemax.png";
      case "VLOC":
        return "../../../../../../../assets/VLOC.png";
      case "VLCC":
        return "../../../../../../../assets/VLCC.png";
      default:
        break;
    }
  };

  const CalculatorSchema = Yup.object().shape({
    vesselType: Yup.string().required("Vessel type is required"),
    vesselSpeed: Yup.string().required("Vessel speed is required"),
  });

  const defaultValues = {
    vesselType: vessel?.vesselType,
    vesselSpeed: vessel?.vesselSpeed,
  };

  const methods = useForm({
    resolver: yupResolver(CalculatorSchema),
    defaultValues,
  });

  const { reset, setValue, setError, watch, handleSubmit, control } = methods;

  const selectedVessel = watch("vesselType");
  const vesselSpeed = watch("vesselSpeed");

  const onSubmit = async (formData) => {
    try {
      const percentSavings = handleCalculate({
        ...formData,
        vesselSpeed: vesselSpeed.toString(),
      });
      const data = {
        ...formData,
        percentSavings,
      };

      setVessel(data);
    } catch (error) {
      console.error(error);

      reset();

      if (isMountedRef.current) {
        setError("afterSubmit", { ...error, message: error.message });
      }
    }
  };

  React.useEffect(() => {
    setValue("vesselSpeed", null);
    setValue(
      "vesselSpeed",
      VESSEL_OPTIONS.find((option) => option.name === selectedVessel)
        ?.speedOptions[0]
    );
    setMinVesselSpeed(
      VESSEL_OPTIONS.find((option) => option.name === selectedVessel)
        ?.speedOptions[0]
    );
    setMaxVesselSpeed(
      VESSEL_OPTIONS.find(
        (option) => option.name === selectedVessel
      )?.speedOptions.at(-1)
    );
  }, [selectedVessel]);

  return (
    <Grid container spacing={0} sx={{ height: "100%" }}>
      <Grid item md={6} sm={12}>
        <Typography
          className="header"
          variant="h3"
          sx={{ width: "50%", margin: "auto", fontWeight: "500", marginBottom: "20px"}}
        >
          Estimated Fuel & Emissions Savings
        </Typography>
          <Box
              sx={{
                  width: "60%",
                  margin: "auto",
                  position: "relative",
                  marginBottom: "120px",
              }}
          >
              <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                  <Card
                      sx={{
                          width: "100%",
                          padding: "1rem",
                      }}
                  >
                      <Grid container spacing={4}>
                          <Grid item xs={6}>
                              <Typography
                                  variant="h6"
                                  sx={{ fontSize: 14, fontWeight: 700 }}
                              >
                                  Vessel Type
                              </Typography>
                              <Stack spacing={3}>
                                  <Box sx={{ paddingTop: "1rem" }}>
                                      <RHFSelect name="vesselType" label="Vessel Type">
                                          <option />
                                          {VESSEL_OPTIONS.map((option) => (
                                              <option key={option.name} value={option.name}>
                                                  {option.name}
                                              </option>
                                          ))}
                                      </RHFSelect>
                                  </Box>
                                  <Button
                                      sx={{
                                          width: "90%",
                                          height: 45,
                                          borderRadius: 2,
                                          mt: 1,
                                          mb: 1,
                                          backgroundColor: "#5ED5A2",
                                          "&:hover": { backgroundColor: "#5ED5A2" },
                                      }}
                                      size="medium"
                                      type="submit"
                                      variant="contained"
                                  >
                                      <Typography
                                          sx={{
                                              fontSize: 12,
                                              fontWeight: "500",
                                              fontFamily: "Rubik",
                                              color: "white",
                                              textTransform: "none",
                                          }}
                                          color="inherit"
                                      >
                                          Update your calculation
                                      </Typography>
                                  </Button>
                              </Stack>
                          </Grid>
                          <Grid item xs={6}>
                              <Typography
                                  variant="h6"
                                  sx={{ fontSize: 14, fontWeight: 700 }}
                              >
                                  Vessel Speed
                              </Typography>
                              {defaultValues?.vesselSpeed && vesselSpeed && (
                                  <Box sx={{ textAlign: "center", width: "60%" }}>
                                      <Controller
                                          name="vesselSpeed"
                                          control={control}
                                          render={({ field, fieldState: { error } }) => (
                                              <Slider
                                                  sx={{ color: "#5ED5A2" }}
                                                  value={vesselSpeed}
                                                  defaultValue={defaultValues?.vesselSpeed}
                                                  min={minVesselSpeed}
                                                  step={1}
                                                  max={maxVesselSpeed}
                                                  onChange={(event, value) =>
                                                      setValue("vesselSpeed", value)
                                                  }
                                              />
                                          )}
                                      />
                                      <Typography
                                          sx={{
                                              fontSize: 16,
                                              fontWeight: 700,
                                              color: "#5ED5A2",
                                          }}
                                      >
                                          {vesselSpeed} kts
                                      </Typography>
                                  </Box>
                              )}
                          </Grid>
                      </Grid>
                      <Box
                          sx={{
                              position: "absolute",
                              bottom: "-100px",
                              right: "-90px",
                              width: "180px",
                              height: "180px",
                              backgroundColor: "#5ED5A2",
                              borderRadius: 100,
                              zIndex: 20,
                              textAlign: "center",
                              lineHeight: 100,
                          }}
                      >
                          <Typography
                              sx={{
                                  fontSize: 30,
                                  fontWeight: 700,
                                  color: "white",
                                  paddingTop: "65px",
                              }}
                          >
                              {vessel?.percentSavings}%
                          </Typography>
                      </Box>
                  </Card>
              </FormProvider>
          </Box>
        <Typography
          className="paragraph"
          variant="h6"
          sx={{
            textAlign: "left",
            width: "80%",
            margin: "auto",
            my: 2,
            fontSize: 16,
            fontWeight: 400,
          }}
        >
          Anemoi estimate the expected fuel and emissions savings using our
          in-house Fuel Saving Assessment Method. This method combines the
          following 4 sets of input data:
        </Typography>
        <ul className="list">
          <li>Rotor Sail performance data, from our UK test facility</li>
          <li>
            Vessel performance data, from the Client or Clarksons Research World
            Fleet Register
          </li>
          <li>Route data, from the Client or third Party AIS provider</li>
          <li>Wind data, from ERA5 dataset</li>
        </ul>
        <Typography
          className="paragraph"
          variant="h6"
          sx={{
            width: "80%",
            textAlign: "left",
            margin: "auto",
            my: 2,
            fontSize: 16,
            fontWeight: 400,
          }}
        >
          We present net savings, which take into account aspects such as
          headwind drag and increased generator usage to give a fair and
          transparent estimation of performance.
        </Typography>


        <Box sx={{ textAlign: "center" }}>
          <Button
            onClick={() =>
              window.open(
                "https://anemoimarine.com/sustainable-shipping/",
                "_blank"
              )
            }
            sx={{
              color: "#5ED5A2",
              width: "20%",
              marginLeft: "auto",
              marginTop: "2%",
              borderRadius: 30,
              backgroundColor: "transparent",
              borderColor: "red",
              border: 2,
              minWidth: "135px",
            }}
            size="medium"
            variant="outlined"
          >
            <Typography
              sx={{
                fontSize: 14,
                fontFamily: "Rubik",
                color: "#EEF1F3",
                textTransform: "none",
              }}
            >
              Learn More
            </Typography>
            <KeyboardArrowRightIcon sx={{ color: "#EEF1F3" }} />
          </Button>
        </Box>
      </Grid>
      <Grid item md={6} sm={12}>
        <Hidden only={["sm", "xs"]}>
          <Box
            style={{
              height: "100vh",
              position: "fixed",
              top: 0,
              backgroundImage: `url(${determineVesselImage()})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              width: "100%",
            }}
          />
        </Hidden>
      </Grid>
    </Grid>
  );
}
